import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { notification } from "antd";
import { connect, useDispatch } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";

import {
  generateJobShiftPreStartChecklistPdf,
  saveJobShiftPreStartChecklistResponses
} from "../../../../../actions/v2/JobShiftPreStartActions";
import {
  buildPreStartChecklistSavePayload,
  buildChecklistResponseInitialValues,
  buildPreStartQuestionEntries,
  hasPreStartChecklistSaveChanges,
  serializePreStartChecklistResponses
} from "./jobShiftPreStartFormUtils";
import styles from "./JobShiftPreStartView.module.css";
import JobShiftPreStartViewHeader from "./JobShiftPreStartViewHeader.jsx";
import PreStartDetails from "./sections/PreStartDetails.jsx";
import PreStartQuestionsBlock from "./sections/PreStartQuestionsBlock.jsx";
import FlaggedIncorrectiveQuestions from "./sections/FlaggedIncorrectiveQuestions";
import PreStartSignOff from "./sections/PreStartSignOff";

function JobShiftPreStartViewForm({ checklist, sections, savedChecklist, onBack, onClose, onChecklistChange, handleSubmit, change, reset, hasPendingSaveChanges }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const headingContents = checklist && checklist.meta && Array.isArray(checklist.meta.heading_contents)
    ? checklist.meta.heading_contents
    : [];
  const questionEntries = useMemo(() => buildPreStartQuestionEntries(checklist || {}), [checklist]);

  const handleDownload = () => {
    dispatch(generateJobShiftPreStartChecklistPdf({
      jobId: checklist && checklist.job_id,
      shiftId: checklist && checklist.shift_id,
      swmsId: checklist && checklist.swms_id
    })).catch((error) => {
      notification.error({
        message: "Error",
        description: typeof error === "string" ? error : "There was an error while generating the Pre-Start Checklist PDF.",
        className: "ant-error"
      });
    });
  };

  const onSubmit = (values) => {
    if (saving || !savedChecklist) {
      return Promise.resolve();
    }

    const savePayload = buildPreStartChecklistSavePayload(values, savedChecklist);
    if (!hasPreStartChecklistSaveChanges(savePayload)) {
      return Promise.resolve();
    }

    setSaving(true);

    return dispatch(saveJobShiftPreStartChecklistResponses({
      jobId: savedChecklist && savedChecklist.job_id,
      shiftId: savedChecklist && savedChecklist.shift_id,
      swmsId: savedChecklist && savedChecklist.swms_id,
      payload: savePayload
    })).then((res = {}) => {
      const nextSavedChecklist = res && res.pre_start_checklist
        ? res.pre_start_checklist
        : (res && res.checklist ? res.checklist : null);

      if (!nextSavedChecklist) {
        return Promise.reject("Updated Pre-Start checklist data was not returned by the server.");
      }

      if (onChecklistChange) {
        onChecklistChange(nextSavedChecklist);
      }

      notification.success({
        message: "Success",
        description: res && res.message ? res.message : "Pre-Start responses updated successfully.",
        className: "ant-success"
      });
    }).catch((error) => {
      notification.error({
        message: "Error",
        description: typeof error === "string" ? error : "There was an error while saving the Pre-Start responses.",
        className: "ant-error"
      });
    }).finally(() => {
      setSaving(false);
    });
  };

  const handleDiscard = () => {
    if (saving || !hasPendingSaveChanges) {
      return;
    }

    reset();
  };

  return (
    <form className={styles.viewPreview} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.PreviewPlatformBar}>
        <div>
          <button className={styles.PreviewPlatformButton} onClick={onClose || onBack} type="button">
            <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path fill="currentColor" d="M73.4 297.4C60.9 309.9 60.9 330.2 73.4 342.7L233.4 502.7C245.9 515.2 266.2 515.2 278.7 502.7C291.2 490.2 291.2 469.9 278.7 457.4L173.3 352L544 352C561.7 352 576 337.7 576 320C576 302.3 561.7 288 544 288L173.3 288L278.7 182.6C291.2 170.1 291.2 149.8 278.7 137.3C266.2 124.8 245.9 124.8 233.4 137.3L73.4 297.3z" /></svg>
            <span>Back</span>
          </button>
        </div>
        <div className={styles.PreviewPlatformBarRight}>
          {hasPendingSaveChanges ? <div className={styles.PreviewDirtyNotice}>Save your changes to download the Pre start document</div> : null}
          {hasPendingSaveChanges ? (
            <button className={styles.PreviewPlatformButton} disabled={saving} onClick={handleDiscard} type="button">
              <span>Discard</span>
            </button>
          ) : null}
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
          <button className={styles.PreviewPlatformButton} style={{ background: "#6366F1", color: "#fff", borderColor: "#6366F1" }} disabled={saving || !checklist || !hasPendingSaveChanges} type="submit">
            <svg height="18px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path fill="currentColor" d="M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 237.3C544 220.3 537.3 204 525.3 192L448 114.7C436 102.7 419.7 96 402.7 96L160 96zM192 192C192 174.3 206.3 160 224 160L384 160C401.7 160 416 174.3 416 192L416 256C416 273.7 401.7 288 384 288L224 288C206.3 288 192 273.7 192 256L192 192zM320 352C355.3 352 384 380.7 384 416C384 451.3 355.3 480 320 480C284.7 480 256 451.3 256 416C256 380.7 284.7 352 320 352z" />
            </svg>
            <span>Save</span>
          </button>
          <button className={styles.PreviewPlatformButton} style={{ background: "#6366F1", color: "#fff", borderColor: "#6366F1" }} disabled={!checklist || saving || hasPendingSaveChanges} onClick={handleDownload} type="button">
            <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path fill="currentColor" d="M128 128C128 92.7 156.7 64 192 64L341.5 64C358.5 64 374.8 70.7 386.8 82.7L493.3 189.3C505.3 201.3 512 217.6 512 234.6L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 128zM336 122.5L336 216C336 229.3 346.7 240 360 240L453.5 240L336 122.5zM303 505C312.4 514.4 327.6 514.4 336.9 505L400.9 441C410.3 431.6 410.3 416.4 400.9 407.1C391.5 397.8 376.3 397.7 367 407.1L344 430.1L344 344C344 330.7 333.3 320 320 320C306.7 320 296 330.7 296 344L296 430.1L273 407.1C263.6 397.7 248.4 397.7 239.1 407.1C229.8 416.5 229.7 431.7 239.1 441L303.1 505z" />
            </svg>
            <span>Download</span>
          </button>
        </div>
      </div>

      <div className={styles.PreviewWrap}>
        <main>
          <div className={styles.PreviewDoc}>
            <JobShiftPreStartViewHeader sections={sections} preStartData={checklist} />
            <PreStartDetails sections={sections} preStartData={checklist} />
            <PreStartQuestionsBlock preStartData={checklist} questions={questionEntries} formChange={change} />
            <FlaggedIncorrectiveQuestions questions={questionEntries} />
            <PreStartSignOff preStartData={checklist} headings={headingContents} />
          </div>
        </main>
      </div>
    </form>
  );
}

const ReduxJobShiftPreStartViewForm = reduxForm({
  form: "job_shift_pre_start_checklist_form",
  enableReinitialize: true,
  destroyOnUnmount: true
})(JobShiftPreStartViewForm);

const selector = formValueSelector("job_shift_pre_start_checklist_form");

const ConnectedJobShiftPreStartViewForm = connect((state, ownProps) => {
  const liveResponses = selector(state, "responses");
  const liveChecklistHeading = selector(state, "prestart_checklist_heading");
  const liveStaffSignoffHeading = selector(state, "prestart_staff_signoff");
  const formValues = {
    responses: liveResponses || {},
    prestart_checklist_heading: liveChecklistHeading,
    prestart_staff_signoff: liveStaffSignoffHeading
  };
  const liveChecklist = ownProps && ownProps.checklist
    ? serializePreStartChecklistResponses(formValues, ownProps.checklist)
    : ownProps.checklist;
  const savePayload = ownProps && ownProps.checklist
    ? buildPreStartChecklistSavePayload(formValues, ownProps.checklist)
    : { answer_updates: [] };

  return {
    checklist: liveChecklist,
    savedChecklist: ownProps && ownProps.checklist ? ownProps.checklist : null,
    hasPendingSaveChanges: hasPreStartChecklistSaveChanges(savePayload)
  };
})(ReduxJobShiftPreStartViewForm);

export default function JobShiftPreStartView({ checklist, sections, onBack, onClose }) {
  const [currentChecklist, setCurrentChecklist] = useState(checklist || null);

  useEffect(() => {
    setCurrentChecklist(checklist || null);
  }, [checklist]);

  const initialValues = useMemo(() => buildChecklistResponseInitialValues(currentChecklist || {}), [currentChecklist]);

  return (
    <ConnectedJobShiftPreStartViewForm
      checklist={currentChecklist}
      initialValues={initialValues}
      sections={sections}
      onBack={onBack}
      onClose={onClose}
      onChecklistChange={setCurrentChecklist}
    />
  );
}
